import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { toast } from "react-hot-toast";

import FormField from "@mhj/shared/FormField";
import { apiRequest, setCsrfToken } from "@mhj/shared/api";
import { useAuth } from "@mhj/shared/auth";
import type { AuthUser } from "@mhj/shared/types";

export default function Login() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<{ email: string; password: string }>({
    defaultValues: { email: "", password: "" },
  });
  const loginMutation = useMutation({
    mutationFn: (payload: { email: string; password: string }) =>
      apiRequest<{ user: AuthUser; csrfToken: string }>("/auth/login", {
        method: "POST",
        data: payload,
      }),
    onSuccess: async (response) => {
      setCsrfToken(response.csrfToken);
      await refresh();
      navigate(response.user.role === "user" ? "/portal" : "/admin", {
        replace: true,
      });
    },
  });

  const onSubmit = async (values: { email: string; password: string }) => {
    try {
      await loginMutation.mutateAsync(values);
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-sky-50 px-6">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="glass-card w-full max-w-md rounded-3xl p-8"
      >
        <p className="text-xs uppercase tracking-[0.3em] text-blue-300">
          Welcome back
        </p>
        <h1 className="mt-3 text-2xl font-semibold text-slate-900">
          Sign in to Jantabo Scholarship Initiative Portal
        </h1>
        <p className="mt-2 text-sm text-slate-500">
          Access your scholarship application or admin dashboard.
        </p>
        <div className="mt-6 flex flex-col gap-4">
          <FormField label="Email">
            <input
              type="email"
              {...register("email", { required: "Email is required" })}
              className="rounded-2xl border border-blue-200 bg-white px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none"
              placeholder="you@example.com"
            />
          </FormField>
          {errors.email ? (
            <p className="text-xs text-red-200">{errors.email.message}</p>
          ) : null}
          <FormField label="Password">
            <input
              type="password"
              {...register("password", { required: "Password is required" })}
              className="rounded-2xl border border-blue-200 bg-white px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none"
              placeholder="••••••••"
            />
          </FormField>
          {errors.password ? (
            <p className="text-xs text-red-200">{errors.password.message}</p>
          ) : null}
        </div>
        <button
          type="submit"
          disabled={loginMutation.isPending}
          className="mt-6 w-full rounded-full bg-blue-600 px-4 py-3 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-blue-500 disabled:cursor-not-allowed disabled:bg-blue-200"
        >
          {loginMutation.isPending ? "Signing in..." : "Sign in"}
        </button>
        <div className="mt-6 flex items-center justify-between text-xs text-slate-500">
          <span>New applicant?</span>
          <Link to="/register" className="text-blue-600 hover:text-blue-700">
            Create account
          </Link>
        </div>
      </form>
    </div>
  );
}
